'use client';

import { c as _c } from "react/compiler-runtime";
import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import { FC, useRef } from 'react';
import Card from '@/components/service-pages/ConvergingCardRows/Card';
import { ConvergingCardRowsDto } from '@/components/service-pages/ConvergingCardRows/types';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import { gsap } from '@/lib/gsap';
import css from './style.module.scss';
const ANIMATION_DURATION = 1;
const OPACITY_DELAY = 0.3;
interface Props {
  data: ConvergingCardRowsDto;
  extraTopPaddingMobile?: boolean;
}
const ConvergingCardRows: FC<Props> = t0 => {
  const $ = _c(24);
  const {
    data,
    extraTopPaddingMobile
  } = t0;
  const sectionRef = useRef(null);
  const topRowRef = useRef(null);
  const bottomRowRef = useRef(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const mm = gsap.matchMedia();
      mm.add({
        isDesktop: "(min-width: 992px)",
        isMobile: "(max-width: 991px)"
      }, ctx => {
        const {
          isMobile
        } = ctx.conditions as {
          isMobile: boolean;
        };
        const titleLines = sectionRef.current?.querySelectorAll(`.${css.title} p`);
        const description = sectionRef.current?.querySelector(`.${css.description}`);
        if (titleLines?.length) {
          gsap.set(titleLines, {
            opacity: 0,
            y: 40,
            filter: "blur(10px)"
          });
        }
        if (description) {
          gsap.set(description, {
            opacity: 0,
            y: 20
          });
        }
        const tlText = gsap.timeline({
          scrollTrigger: {
            trigger: titleLines,
            start: "top 80%",
            toggleActions: "play none none reverse"
          }
        });
        if (titleLines?.length) {
          tlText.to(titleLines, {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 1.1,
            ease: "power3.out",
            stagger: 0.15
          });
        }
        if (description) {
          tlText.to(description, {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "power3.out"
          }, "-=0.8");
        }
        if (isMobile) {
          return;
        }
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom",
            end: "center center",
            scrub: true
          }
        });
        tl.fromTo(topRowRef.current, {
          xPercent: -120
        }, {
          xPercent: 0,
          ease: "none",
          duration: ANIMATION_DURATION
        }, 0).fromTo(bottomRowRef.current, {
          xPercent: 120,
          y: -0.5
        }, {
          xPercent: 0,
          y: -0.5,
          ease: "none",
          duration: ANIMATION_DURATION
        }, 0).fromTo([topRowRef.current, bottomRowRef.current], {
          opacity: 0
        }, {
          opacity: 1,
          ease: "power1.inOut",
          duration: ANIMATION_DURATION - OPACITY_DELAY
        }, OPACITY_DELAY);
      });
      return () => mm.revert();
    };
    t2 = {
      scope: sectionRef
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useGSAP(t1, t2);
  const t3 = extraTopPaddingMobile && css.extraTopPaddingMobile;
  let t4;
  if ($[2] !== t3) {
    t4 = clsx(css.section, "container", t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== data.title) {
    t5 = <Typography uppercase={true} variant="h2" tag="h2" className={css.title}><StrapiRichContent content={data.title} /></Typography>;
    $[4] = data.title;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== data.description) {
    t6 = <Typography variant="body-m" tag="div" className={css.description}><StrapiRichContent content={data.description} /></Typography>;
    $[6] = data.description;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== data.cards) {
    t7 = data.cards.slice(0, 3).map(_temp);
    $[8] = data.cards;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t7) {
    t8 = <div ref={topRowRef} className={css.row}>{t7}</div>;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== data.cards) {
    t9 = data.cards.slice(3, 6).map(_temp2);
    $[12] = data.cards;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t9) {
    t10 = <div ref={bottomRowRef} className={css.row}>{t9}</div>;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t8) {
    t11 = <div className={css.grid}>{t8}{t10}</div>;
    $[16] = t10;
    $[17] = t8;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11 || $[20] !== t4 || $[21] !== t5 || $[22] !== t6) {
    t12 = <section ref={sectionRef} className={t4}>{t5}{t6}{t11}</section>;
    $[19] = t11;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
};
export default ConvergingCardRows;
function _temp(card, index) {
  return <Card key={index} data={card} />;
}
function _temp2(card_0, index_0) {
  return <Card key={index_0} data={card_0} />;
}